import type { BffLogicalDeviceResourceReadApi } from '@ingenico/api-sdk-provisioning-private';
import type {
  BusinessServiceRequest,
  BusinessServiceRequestWithFirstQueryLoading,
} from '@ingenico/react-section-utils';
import { useBusinessServiceRequest, useQueryFirstLoading } from '@ingenico/react-section-utils';
import { usePrevious } from '@react-hookz/web';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { DeviceManagementService } from '../../../domain/business/services/deviceManagement.service';
import type { DeviceConnectionStatus, DeviceMetrics } from '../../../domain/models';
import { enumDeviceFamily } from '../../../domain/models';
import type { DeviceInfo } from '../../../domain/models/deviceInfo.model';
import { useDeviceScreenShareFeature } from '../../../hooks/useDeviceScreenShareFeature.hook';
import { useLogicalDeviceGeofendingDetails } from '../../../hooks/useLogicalDeviceGeofendingDetails.hook';
import { useLogicalDevicesDetails } from '../../../hooks/useLogicalDevicesDetails.hook';
import { useDeviceManagementCapability } from '../../../hooks/useDeviceManagementCapability.hook';
import { useLogicalDeviceApplicationsCard } from '../../logical-device-applications-card/useLogicalDeviceApplicationsCard.vm';
import { LogicalDeviceConnectivityStatus } from '../../logical-device-summary-card/logicalDeviceSummaryCard.model';
import type { UseLogicalDeviceInfoAttachedVmReturn } from './LogicalDeviceInfo.model';
import {
  checkShouldShowLogicalDevice,
  checkShouldShowTerminalDetails,
  getDeviceBatteryInformation,
  getDeviceConnectivityData,
  getDeviceHardwareCharts,
  getDeviceSIMInformation,
  getSummaryGeneralData,
  hasScreenLockingStatusSettled,
} from './logicalDeviceInfo.util';

export function useLogicalDeviceInfoAttachedVm({
  logicalDeviceId,
  logicalDeviceDetailsQuery,
  deviceIdV2,
}: {
  readonly logicalDeviceId: string;
  readonly logicalDeviceDetailsQuery: BusinessServiceRequest<BffLogicalDeviceResourceReadApi>;
  readonly deviceIdV2: BusinessServiceRequestWithFirstQueryLoading<DeviceInfo>;
}): UseLogicalDeviceInfoAttachedVmReturn {
  const location = useLocation();

  const {
    data: logicalDeviceDetails,
    isLoading: isLoadingLogicalDeviceInfoVM,
    isError: isLogicalDeviceInfoVMError,
    error: logicalDeviceError,
    isSuccess: isLogicalDeviceDetailsSuccess,
  } = useMemo(() => logicalDeviceDetailsQuery, [logicalDeviceDetailsQuery]);

  const physicalDeviceId = useMemo(() => {
    const attachedDevice = logicalDeviceDetails?.attachedPhysicalDevices?.[0];
    if (attachedDevice?.id) {
      return attachedDevice.id;
    }
    return null;
  }, [logicalDeviceDetails?.attachedPhysicalDevices]);

  const {
    data: physicalDeviceInfo,
    isLoading: isLoadingPhysicalDeviceInfo,
    isError: isPhysicalDeviceInfoError,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) => {
        if (!physicalDeviceId) {
          return null;
        }
        return deviceManagementService.getPhysicalDeviceInformation(physicalDeviceId);
      },
      [physicalDeviceId],
    ),
  );

  const isTetraDevice = useMemo(() => {
    if (!physicalDeviceInfo?.deviceType) {
      return false;
    }
    return (
      !isLoadingPhysicalDeviceInfo &&
      !isPhysicalDeviceInfoError &&
      physicalDeviceInfo.deviceType === enumDeviceFamily.TETRA
    );
  }, [physicalDeviceInfo?.deviceType, isLoadingPhysicalDeviceInfo, isPhysicalDeviceInfoError]);

  const hasDeviceStateReadPermission = useDeviceManagementCapability('logicalDevice.deviceStateRead');

  const isLayoutDecisionReady = useMemo(() => {
    return !isLoadingPhysicalDeviceInfo && !!logicalDeviceDetails && hasDeviceStateReadPermission !== undefined;
  }, [isLoadingPhysicalDeviceInfo, logicalDeviceDetails, hasDeviceStateReadPermission]);

  const shouldDisplayMinimalDeviceLayout = useMemo(() => {
    if (!isLayoutDecisionReady) {
      return false;
    }
    return !hasDeviceStateReadPermission && !!logicalDeviceDetails;
  }, [hasDeviceStateReadPermission, logicalDeviceDetails, isLayoutDecisionReady]);

  const shouldFetch = useMemo(
    () => isLayoutDecisionReady && !shouldDisplayMinimalDeviceLayout,
    [isLayoutDecisionReady, shouldDisplayMinimalDeviceLayout],
  );

  const { refetch: refetchSoftware, ...logicalDeviceProps } = useLogicalDeviceApplicationsCard({
    shouldFetch,
  });

  const { handleRefreshDeviceProperties, logicalDeviceConfiguration } = useLogicalDevicesDetails({
    logicalDeviceId,
    onAskRefetchSuccess: refetchSoftware,
    shouldFetch,
  });

  const {
    callScheduling,
    connectionStatus,
    deviceMetrics,
    isRtsEnabled,
    isLoadingDeviceConfiguration,
    isRefetchingDeviceConfiguration,
    screenLockingStatus,
    isDeviceMetricsSuccessfull,
  } = useMemo(() => logicalDeviceConfiguration, [logicalDeviceConfiguration]);

  const previousScreenLockingStatus = usePrevious(screenLockingStatus);
  useEffect(() => {
    if (hasScreenLockingStatusSettled(previousScreenLockingStatus, screenLockingStatus)) {
      logicalDeviceDetailsQuery.refetch();
    }
  }, [logicalDeviceDetailsQuery, previousScreenLockingStatus, screenLockingStatus]);

  const isFirstLoading = useQueryFirstLoading(logicalDeviceDetailsQuery);

  const handleRefreshConfigurations = useCallback(() => {
    handleRefreshDeviceProperties();
  }, [handleRefreshDeviceProperties]);

  const isRemoteDesktopSignatureDevicePermissionLoading = useMemo(() => {
    return deviceIdV2?.isLoading;
  }, [deviceIdV2?.isLoading]);

  const {
    isDeviceAllowedToOpenScreenShare,
    isModalOpen: isShareScreenModalOpen,
    isScreenShareAvailable,
    isScreenShareSessionOpened,
  } = useDeviceScreenShareFeature({
    isTetraDevice,
    remoteDesktopSignatureDevicePermission: deviceIdV2?.data?.remoteDesktopStatus,
    remoteDesktopSignatureDevicePermissionError: deviceIdV2?.isError,
  });

  const isLoading = useMemo(
    () =>
      isLoadingLogicalDeviceInfoVM ||
      isLoadingDeviceConfiguration ||
      isRemoteDesktopSignatureDevicePermissionLoading ||
      isLoadingPhysicalDeviceInfo,
    [
      isLoadingLogicalDeviceInfoVM,
      isLoadingDeviceConfiguration,
      isRemoteDesktopSignatureDevicePermissionLoading,
      isLoadingPhysicalDeviceInfo,
    ],
  );

  const isError = useMemo(() => isLogicalDeviceInfoVMError, [isLogicalDeviceInfoVMError]);

  const deviceMetricsOutput = useMemo(() => deviceMetrics as DeviceMetrics, [deviceMetrics]);
  const connectionStatusOutput = useMemo(() => connectionStatus as DeviceConnectionStatus, [connectionStatus]);

  const latestConnectivity = useMemo(() => getDeviceConnectivityData(deviceMetricsOutput), [deviceMetricsOutput]);

  const latestGeneral = useMemo(
    () =>
      getSummaryGeneralData(logicalDeviceDetails, deviceMetricsOutput, connectionStatus, null, {
        screenLockingStatus,
      }),
    [connectionStatus, deviceMetricsOutput, logicalDeviceDetails, screenLockingStatus],
  );
  const latestHardware = useMemo(() => getDeviceHardwareCharts(deviceMetricsOutput), [deviceMetricsOutput]);
  const [connectivity, setConnectivity] = useState(latestConnectivity);
  const [general, setGeneral] = useState(latestGeneral);
  const [hardware, setHardware] = useState(latestHardware);
  const [latestDeviceMetrics, setLatestDeviceMetrics] = useState(deviceMetricsOutput);

  useEffect(() => {
    if (isDeviceMetricsSuccessfull) {
      setConnectivity(latestConnectivity);
      setHardware(latestHardware);
      setGeneral(latestGeneral);
      setLatestDeviceMetrics(deviceMetricsOutput);
    }
  }, [deviceMetricsOutput, isDeviceMetricsSuccessfull, latestConnectivity, latestGeneral, latestHardware]);
  useEffect(() => {
    if (deviceIdV2?.isSuccess) {
      setGeneral(latestGeneral);
    }

    if (deviceIdV2?.isError) {
      setGeneral((currGeneral) => ({
        ...currGeneral,
        isConnected: false,
        connectivityStatus: LogicalDeviceConnectivityStatus.Offline,
      }));
    }
  }, [
    deviceIdV2?.isError,
    deviceIdV2?.isSuccess,
    isLogicalDeviceDetailsSuccess,
    isLogicalDeviceInfoVMError,
    latestGeneral,
  ]);

  const shouldShowLogicalDevice = useMemo(
    () => checkShouldShowLogicalDevice(latestDeviceMetrics),
    [latestDeviceMetrics],
  );
  const shouldShowTerminalDetailsByDeviceAttached = useMemo(
    () => checkShouldShowTerminalDetails(latestDeviceMetrics, logicalDeviceDetails?.attachedPhysicalDevices?.[0]),
    [latestDeviceMetrics, logicalDeviceDetails?.attachedPhysicalDevices],
  );

  const shouldShowTerminalDetails = useMemo(() => {
    return shouldShowTerminalDetailsByDeviceAttached;
  }, [shouldShowTerminalDetailsByDeviceAttached]);

  const deviceSIMInformation = useMemo(() => getDeviceSIMInformation(latestDeviceMetrics), [latestDeviceMetrics]);

  const deviceBatteryInformation = useMemo(
    () => getDeviceBatteryInformation(latestDeviceMetrics),
    [latestDeviceMetrics],
  );

  const handleOpenScreenShare = useCallback(() => {
    const decodedPathname = decodeURIComponent(location.pathname);

    const queryParams = new URLSearchParams({
      deviceName: logicalDeviceDetails?.name || '',
      deviceModel: logicalDeviceDetails?.attachedPhysicalDevices?.[0]?.model || '',
      serialNumber: logicalDeviceDetails?.attachedPhysicalDevices?.[0]?.serialNumber || '',
    }).toString();

    const url = `${window.location.origin}${decodedPathname.replace(/\/$/u, '')}/screen-share?${queryParams}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }, [location, logicalDeviceDetails]);

  const isScreenShareEnabled = useMemo<boolean>(() => {
    return isDeviceAllowedToOpenScreenShare && isEqual(connectionStatus?.isConnected, true);
  }, [connectionStatus?.isConnected, isDeviceAllowedToOpenScreenShare]);

  const geofencing = useLogicalDeviceGeofendingDetails(logicalDeviceId);
  const tetraCallScheduling = useMemo(() => callScheduling, [callScheduling]);

  const isToShowDeviceDetails = useMemo(() => {
    return isTetraDevice || isRtsEnabled;
  }, [isRtsEnabled, isTetraDevice]);

  return {
    connectionStatus: connectionStatusOutput,
    connectivity,
    deviceBatteryInformation,
    deviceMetrics: latestDeviceMetrics,
    deviceSIMInformation,
    general,
    geofencing,
    handleOpenScreenShare,
    handleRefreshConfigurations,
    hardware,
    isDeviceAllowedToOpenScreenShare,
    isError,
    isFirstLoading,
    isLoading,
    isRefetchActionOngoing: isRefetchingDeviceConfiguration,
    isRtsEnabled,
    isScreenShareAvailable,
    isScreenShareDisabled: !isScreenShareEnabled,
    isScreenShareSessionOpened,
    isShareScreenModalOpen,
    isTetraDevice,
    isToShowDeviceDetails,
    logicalDeviceDetails,
    logicalDeviceError,
    logicalDeviceProps,
    physicalDeviceInfo,
    tetraCallScheduling,
    shouldDisplayMinimalDeviceLayout,
    shouldShowLogicalDevice,
    shouldShowTerminalDetails,
  };
}
